import { watchExperimentFlags } from '../utils/experimentFlagUpdates';
import { useEffect, useState } from 'react';
import { capturePostHogEvent, getPostHogClient } from '../utils/analytics';
import { isWebsiteThemeTrafficEligible } from './websiteTheme';
import { isPreviewExperimentOverrideHost } from '../utils/experimentOverrideHosts';

export const SIGN_IN_FLAG = 'signin-box-2026-09-13';
export type SignInVariant = 'control' | 'test';
const production = () => isWebsiteThemeTrafficEligible(window.location.hostname);
const storageKey = `botspot:${SIGN_IN_FLAG}`;
const valid = (value: unknown): value is SignInVariant => value === 'control' || value === 'test';
export function signInPreview(): SignInVariant | null {
  if (!isPreviewExperimentOverrideHost()) return null;
  const value = new URLSearchParams(window.location.search).get('signInDesign');
  return value === 'orange' ? 'control' : value === 'clarity' ? 'test' : null;
}
export default function useSignInExperiment(enabled: boolean) {
  const [result, setResult] = useState<{
    variant: SignInVariant;
    enrolled: boolean;
    ready: boolean;
  }>({
    variant: signInPreview() || 'test',
    enrolled: false,
    ready: !enabled || !production(),
  });
  useEffect(() => {
    if (!enabled) return;
    const preview = signInPreview();
    if (preview) {
      setResult({ variant: preview, enrolled: false, ready: true });
      return;
    }
    if (!production()) return;
    const client = getPostHogClient({ initialize: true });
    if (!client) {
      setResult({ variant: 'test', enrolled: false, ready: true });
      return;
    }
    let cancelled = false;
    let ready = false;
    setResult({ variant: 'test', enrolled: false, ready: false });
    const finish = (variant: SignInVariant, enrolled: boolean) => {
      if (cancelled) return;
      ready = true;
      setResult((previous) =>
        previous.ready && previous.variant === variant && previous.enrolled === enrolled
          ? previous
          : { variant, enrolled, ready: true },
      );
    };
    const stop = () => {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* Rendering remains unenrolled. */
      }
      finish('test', false);
    };
    const evaluate = (flagsUpdated = false) => {
      if (cancelled) return;
      let value;
      try {
        value = client.getFeatureFlag(SIGN_IN_FLAG, { send_event: false });
      } catch {
        stop();
        return;
      }
      if (!valid(value)) {
        if (value !== false && !flagsUpdated && !ready) return;
        return stop();
      }
      const variant = value;
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved !== variant) localStorage.setItem(storageKey, variant);
      } catch {
        /* The current PostHog value still applies without storage. */
      }
      finish(variant, true);
    };
    evaluate();
    const unsubscribe = watchExperimentFlags(client, () => evaluate(true));
    const timeout = window.setTimeout(() => {
      if (!ready) stop();
    }, 3000);
    return () => {
      cancelled = true;
      unsubscribe?.();
      window.clearTimeout(timeout);
    };
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !result.ready || !result.enrolled) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        capturePostHogEvent('$feature_flag_called', {
          $feature_flag: SIGN_IN_FLAG,
          $feature_flag_response: result.variant,
          experiment_name: SIGN_IN_FLAG,
          feature_flag: SIGN_IN_FLAG,
          variant: result.variant,
          [`$feature/${SIGN_IN_FLAG}`]: result.variant,
          sign_in_design: result.variant === 'control' ? 'orange' : 'clarity',
        });
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [enabled, result]);
  return {
    ...result,
    ready: !enabled || result.ready,
    design: (enabled && result.variant === 'control' ? 'orange' : 'clarity') as
      | 'orange'
      | 'clarity',
    analytics:
      enabled && result.enrolled
        ? {
            sign_in_experiment: SIGN_IN_FLAG,
            sign_in_variant: result.variant,
            [`$feature/${SIGN_IN_FLAG}`]: result.variant,
          }
        : {},
  };
}
